import { getDateFilter } from "@flexibees/core";
import { API } from "../constants/api";
import {
  axiosGetWithToken,
  axiosPostWithToken,
  axiosPut,
} from "../services/apiServices";
import { ERROR_ALERT, SUCCESS_ALERT } from "./commonAction";

// ─── Action Types ────────────────────────────────────────────────────────────

export const PROJECT_LAUNCH_LIST = "PROJECT_LAUNCH_LIST";
export const PROJECT_LAUNCH_LOADER = "PROJECT_LAUNCH_LOADER";
export const SEARCH_JOBS = "SEARCH_JOBS";
export const CHANGE_CURRENT_PROJECT_PAGE = "CHANGE_CURRENT_PROJECT_PAGE";
export const SET_PROJECT_ITEMS_PER_PAGE = "SET_PROJECT_ITEMS_PER_PAGE";
export const PROJECT_STATUS_FILTER = "PROJECT_STATUS_FILTER";
export const GET_RECRUITMENT = "GET_RECRUITMENT";
export const SET_RECRUITMENT_ITEMS_PER_PAGE = "SET_RECRUITMENT_ITEMS_PER_PAGE";
export const CHANGE_CURRENT_RECRUITMENT_PAGE =
  "CHANGE_CURRENT_RECRUITMENT_PAGE";
export const GET_WORKFLOW_FINAL = "GET_WORKFLOW_FINAL";
export const SET_WORKFLOW_FINAL_ITEMS_PER_PAGE =
  "SET_WORKFLOW_FINAL_ITEMS_PER_PAGE";
export const CHANGE_CURRENT_WORKFLOW_FINAL_PAGE =
  "CHANGE_CURRENT_WORKFLOW_FINAL_PAGE";
export const VIEW_LAUNCH_DETAILS = "VIEW_LAUNCH_DETAILS";
export const CHANGE_VIEW_LAUNCH_TAB = "CHANGE_VIEW_LAUNCH_TAB";
export const PROJECT_RENEWAL_LOADER = "PROJECT_RENEWAL_LOADER";
export const PROJECT_RENEWAL_LIST = "PROJECT_RENEWAL_LIST";
export const SET_LAUNCH_DATE_FILTER = "SET_LAUNCH_DATE_FILTER";
export const CLEAR_LAUNCH_DATE_FILTER = "CLEAR_LAUNCH_DATE_FILTER";



const buildStatusFilter = (statusus) => {
  if (statusus.length === 1) return { status: String(statusus[0]) };
  if (statusus.length > 1) return { statusus: statusus.map(String) };
  return {};
};

const buildDateFilters = (dateFilters = {}) => {
  const result = {};

  Object.entries(dateFilters).forEach(([field, range]) => {
    if (!range) return; // filter cleared / never set -> skip entirely

    const [from, to] = range;
    if (from) result[`${field}_from`] = from;
    if (to) result[`${field}_to`] = to;
  });

  return result;
};


const buildProjectLaunchBody = (getState, extra = {}) => {
  const { projectLaunch } = getState();
  const statusus = projectLaunch.projectLaunchStatusFliter;

  return {
    search: projectLaunch.projectLaunchSearchText,
    page_no: projectLaunch.projectLaunchCurrentPage,
    ...buildStatusFilter(statusus),
      ...buildDateFilters(projectLaunch.dateFilters),
         ...extra,
  };
};


const buildRecruitmentBody = (getState) => {
  const { recruit } = getState();
  const date = getDateFilter(recruit.recruitmentAppliedDateFilter[0]);

  return {
    search_term: recruit.recruitmentSearchText,
    filter_data: {
      functions: recruit.recruitmentAppliedFunctionFilter,
      roles: recruit.recruitmentAppliedRolesFilter,
      status: recruit.recruitmentAppliedStatusFilter,
      recruiters: recruit.recruitmentAppliedRecruiterFilter,
      start_date: date,
      bd_managers: recruit.recruitmentAppliedBDManagerFilter,
      no_of_days: recruit.recruitmentAppliedNoOfDaysFilter,
    },
  };
};


const dispatchError = (dispatch, err, fallback = "Something went wrong.") => {
  dispatch({
    type: ERROR_ALERT,
    payload: err?.response?.data?.message || err?.response?.data || fallback,
  });
};



const buildActiveDateRange = (dateFilters = {}) => {
  const activeEntry = Object.entries(dateFilters).find(
    ([, range]) => range && (range[0] || range[1])
  );

  if (!activeEntry) return {};

  const [, [from, to]] = activeEntry;
  return {
    ...(from && { from }),
    ...(to && { to }),
  };
};

export const getProjectLauchList = () => {
  return async (dispatch, getState) => {
    dispatch({ type: PROJECT_LAUNCH_LOADER });

    const token = getState().auth.token;
    const { projectLaunch } = getState();
    const {
      projectLaunchCurrentPage: pageNumber,
      projectLaunchSearchText: searchText,
      projectLaunchStatusFliter: statusus,
      dateFilters = {},
    } = projectLaunch;

    const bodyData = {
      search: searchText,
      page_no: pageNumber,
      ...buildStatusFilter(statusus),
      ...buildActiveDateRange(dateFilters),
    };

    axiosPostWithToken(API.PROJECT_LAUNCH_LIST, bodyData, token)
      .then((res) => {
        if (res.data.status_code === 200) {
          dispatch({ type: PROJECT_LAUNCH_LIST, payload: res.data.data });
        } else {
          dispatch({
            type: ERROR_ALERT,
            payload: res.data.data.message || "Something went wrong.",
          });
        }
      })
      .catch((err) => dispatchError(dispatch, err));
  };
};

export const exportProjectData =
  (fromDate, toDate) => async (dispatch, getState) => {
    dispatch({ type: PROJECT_LAUNCH_LOADER });

    const token = getState().auth.token;
    const bodyData = buildProjectLaunchBody(getState, {
      from: fromDate,
      to: toDate,
    });

    axiosPostWithToken(API.PROJECT_DETAILS_DOWNLOAD, bodyData, token, {
      responseType: "blob",
    })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "project-data.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => dispatchError(dispatch, err));
  };

export const setItemsPerPage = (value) => async (dispatch) => {
  dispatch({ type: PROJECT_LAUNCH_LOADER });
  dispatch({ type: SET_PROJECT_ITEMS_PER_PAGE, payload: value });
};

export const changeCurrentPage = (value) => async (dispatch) => {
  dispatch({ type: PROJECT_LAUNCH_LOADER });
  dispatch({ type: CHANGE_CURRENT_PROJECT_PAGE, payload: value });
};

export const search = (value) => async (dispatch) => {
  dispatch({ type: PROJECT_LAUNCH_LOADER });
  dispatch({ type: SEARCH_JOBS, payload: value });
};

export const setProjectStatusFliter = (value) => async (dispatch) => {
  dispatch({ type: PROJECT_STATUS_FILTER, payload: value });
};

export const editNotes = (id, data, refresh) => async (dispatch, getState) => {
  const token = getState().auth.token;

  axiosPostWithToken(
    `${API.EDIT_PROJECT_NOTES}/${id}`,
    { notes: data },
    token,
  ).then((res) => {
    if (res.data.status_code === 200) {
      dispatch({ type: PROJECT_LAUNCH_LOADER });
      refresh();
      dispatch({ type: SUCCESS_ALERT, payload: "Notes updated Successfully" });
    } else {
      dispatch({
        type: ERROR_ALERT,
        payload:
          res.data.data.message || "Something went wrong. Error updating User.",
      });
    }
  });
};

// ─── Recruitment ─────────────────────────────────────────────────────────────

/**
 * Shared fetch logic for getRecruitment and getCandidateList.
 * Both hit the same endpoint with the same body — only the page source differs.
 */
const fetchRecruitment = (page) => async (dispatch, getState) => {
  const token = getState().auth.token;
  const { recruit } = getState();
  const pagesize = recruit.recruitmentResultsPerPage;
  const resolvedPage = page ?? recruit.recruitmentCurrentPage;
  const body = buildRecruitmentBody(getState);

  axiosPostWithToken(
    `${API.GET_RECRUITMENT}/${pagesize}/${resolvedPage}/`,
    body,
    token,
  ).then((res) => {
    if (res.data.status_code === 200) {
      dispatch({ type: GET_RECRUITMENT, payload: res.data.data });
    } else {
      dispatch({
        type: ERROR_ALERT,
        payload:
          res.data.data.message ||
          "Something went wrong. Unable to fetch data.",
      });
    }
  });
};


export const getRecruitment = (currentPage) => fetchRecruitment(currentPage);


export const getCandidateList = () => fetchRecruitment(null);

export const loadMoreRecruitment = () => (dispatch, getState) => {
  const { recruitmentCurrentPage, recruitmentTotalPages } = getState().recruit;

  if (recruitmentCurrentPage < recruitmentTotalPages) {
    const nextPage = recruitmentCurrentPage + 1;
    dispatch({ type: CHANGE_CURRENT_RECRUITMENT_PAGE, payload: nextPage });
    dispatch(getRecruitment(nextPage));
  }
};



export const getFinal = (projectId) => async (dispatch, getState) => {
  const token = getState().auth.token;
  const { recruit } = getState();

  const matching =
    recruit.workflowFinalAppliedMatchingFilter === "0" ? false : true;

  const body = {
    search_term: recruit.workflowFinalSearchText,
    filter_data: {
      roles: recruit.workflowFinalAppliedRolesFilter,
      skills: recruit.workflowFinalAppliedSkillsFilter,
      status: recruit.workflowFinalAppliedStatusFilter,
      final_selection: matching,
      flexibees_selected: recruit.candidatesListApplaiedFilterFlexiSelected,
      active_projects: recruit.candidatesListApplaiedFilterActiveProjects,
      relevant_experience: {
        min: recruit.workflowFinalAppliedMinRelExpFilter,
        max: recruit.workflowFinalAppliedMaxRelExpFilter,
      },
      years_of_experience: {
        min: recruit.workflowFinalAppliedMinTotalExpFilter,
        max: recruit.workflowFinalAppliedMaxTotalExpFilter,
      },
      total_available_hours: recruit.workflowFinalAppliedAvailableHoursFilter,
    },
  };

  axiosPostWithToken(
    `${API.GET_WORKFLOW_FINAL}/${projectId}/${recruit.workflowFinalResultsPerPage}/${recruit.workflowFinalCurrentPage}/`,
    body,
    token,
  ).then((res) => {
    if (res.data.status_code === 200) {
      dispatch({ type: GET_WORKFLOW_FINAL, payload: res.data.data });
    } else {
      dispatch({
        type: ERROR_ALERT,
        payload:
          res.data?.data?.message ||
          "Something went wrong. Unable to fetch data.",
      });
    }
  });
};



export const createLaunchProject =
  (id, data, refresh) => async (dispatch, getState) => {
    const token = getState().auth.token;

    const payload = {
      latest_start_date: data?.starting_date,
      latest_project_hours: data?.working_hours,
      status: data?.status,
      notes: data?.notes,
    };

    axiosPostWithToken(
      `${API.CREATE_PROJECT_LAUNCH}${id}/`,
      payload,
      token,
    ).then((res) => {
      if (res.data.status_code === 200) {
        dispatch({ type: PROJECT_LAUNCH_LOADER });
        refresh();
        dispatch({
          type: SUCCESS_ALERT,
          payload: res.data.data.message || "Successfully updated",
        });
      } else {
        dispatch({
          type: ERROR_ALERT,
          payload:
            res.data.data.message ||
            "Something went wrong. Error updating User.",
        });
      }
    });
  };

export const updateDetails =
  (id, data, refresh) => async (dispatch, getState) => {
    const token = getState().auth.token;
    const payload = { ...data };


    [
      "latest_start_date",
      "termination_date",
      "tentative_end_date",
      "original_end_date",
    ].forEach((field) => {
      if (!payload[field]) payload[field] = null;
    });

    if (
      payload.latest_project_hours?.trim() &&
      !payload.latest_project_hours.toLowerCase().includes("hours per day")
    ) {
      payload.latest_project_hours = `${payload.latest_project_hours} hours per day`;
    }

    axiosPostWithToken(
      `${API.UPDATE_PROJECT_LAUNCH}/${id}/`,
      payload,
      token,
    ).then((res) => {
      if (res.data.status_code === 200) {
        dispatch({ type: PROJECT_LAUNCH_LOADER });
        refresh();
        dispatch({
          type: SUCCESS_ALERT,
          payload: "Notes updated Successfully",
        });
      } else {
        dispatch({
          type: ERROR_ALERT,
          payload:
            res.data.data.message ||
            "Something went wrong. Error updating User.",
        });
      }
    });
  };

export const createProjectRenewal =
  (id, data, history, path) => async (dispatch, getState) => {
    const token = getState().auth.token;

    const payload = {
      renewal_period: data?.duration,
      new_client_pricing: data?.new_client_pricing,
      new_candidate_pricing: data?.new_candidate_pricing,
      new_project_hours: data?.working_hours,
      status: data?.status,
      ...(data?.renewal_date && { date_of_renewal: data.renewal_date }),
    };

    axiosPostWithToken(
      `${API.CREATE_PROJECT_RENEWALS}${id}/`,
      payload,
      token,
    ).then((res) => {
      if (res.data.status_code === 200) {
        dispatch({ type: PROJECT_LAUNCH_LOADER });
        dispatch({
          type: SUCCESS_ALERT,
          payload: res.data.data.message || "Successfully updated",
        });
        history.push(`${path}/detail/${id}`);
      } else {
        dispatch({
          type: ERROR_ALERT,
          payload:
            res.data.data.message ||
            "Something went wrong. Error updating User.",
        });
      }
    });
  };

export const viewLaunchDetails = (id) => async (dispatch, getState) => {
  const token = getState().auth.token;

  axiosGetWithToken(`${API.PROJECT_LAUNCH_DETAILS}/${id}/`, token)
    .then((res) => {
      if (res.data.status_code === 200) {
        dispatch({ type: VIEW_LAUNCH_DETAILS, payload: res.data.data });
      } else {
        dispatch({
          type: ERROR_ALERT,
          payload: res.data.data.message || "Something went wrong.",
        });
      }
    })
    .catch((err) => dispatchError(dispatch, err));
};

export const changeviewProjectTab = (id) => async (dispatch) => {
  dispatch({ type: CHANGE_VIEW_LAUNCH_TAB, payload: id });
};

export const getProjectRenewalList =
  (launchId) => async (dispatch, getState) => {
    dispatch({ type: PROJECT_RENEWAL_LOADER });

    const token = getState().auth.token;

    axiosGetWithToken(`${API.PROJECT_RENEWALS_LIST}${launchId}/`, token)
      .then((res) => {
        const apiData = res?.data?.data || [];
        if (res?.data?.status_code === 200 && Array.isArray(apiData)) {
          dispatch({ type: PROJECT_RENEWAL_LIST, payload: apiData });
        } else {
          dispatch({
            type: ERROR_ALERT,
            payload: res?.data?.message || "Failed to load renewals.",
          });
        }
      })
      .catch((err) =>
        dispatchError(
          dispatch,
          err,
          "Something went wrong while fetching renewals.",
        ),
      );
  };

export const updateRenewal =
  (launchId, data, id) => async (dispatch, getState) => {
    const token = getState().auth.token;

    const bodyData = {
      ...(data.renewal_date && { date_of_renewal: data.renewal_date }),
      ...(data.duration && { renewal_period: data.duration }),
      ...(data.new_client_pricing && {
        new_client_pricing: data.new_client_pricing,
      }),
      ...(data.new_candidate_pricing && {
        new_candidate_pricing: data.new_candidate_pricing,
      }),
      ...(data.working_hours && { new_project_hours: data.working_hours }),
      ...(data.status && { status: data.status }),
    };

    axiosPut(`${API.UPDATE_RENEWALS}${launchId}/`, bodyData, token)
      .then((res) => {
        if (res?.data?.status_code === 200) {
          dispatch({
            type: SUCCESS_ALERT,
            payload: "Renewal updated successfully.",
          });
          dispatch(getProjectRenewalList(id));
        } else {
          dispatch({
            type: ERROR_ALERT,
            payload: res?.data?.message || "Failed to Update Renewal.",
          });
        }
      })
      .catch((err) =>
        dispatchError(
          dispatch,
          err,
          "Something went wrong while updating the Renewal.",
        ),
      );
  };
